import React, { useState, useRef, useEffect } from 'react'
import { Formik, Form, FormikHelpers } from 'formik'
import { useTranslation } from 'react-i18next'
import { Button, Heading, Loading } from '@pandiafi/design-system'
import { Alert } from '@pandiafi/components'

import { getValidationSchema } from './validationSchema'
import { useSendMessage } from './api'
import { initialValues, MessageFormValues } from './types'
import { MessageCategoryField } from './MessageCategoryField/MessageCategoryField'
import { MessageCategory } from '../../../common/validations/residentMessageTimelineSchema'

import { useAPIGetResidentMessageCategories } from 'api/apiHooks/useAPIGetResidentMessageCategories'
import { FilesField } from 'components/FilesField/Index'
import { FormTextField } from 'components/FormTextField'
import { FormRichTextField } from 'components/FormRichtextField'

interface MessageFormContainerProps {
  threadId: string | undefined
  onSuccess?: () => void
  onCancel?: () => void
  replySubject?: string
  focusSubject?: boolean
  messageCategory?: MessageCategory | null
  showParticipantsNotice: boolean
}

const getInitialMessageCategoryId = ({
  threadId,
  messageCategory,
}: Pick<MessageFormContainerProps, 'threadId' | 'messageCategory'>): string => {
  if (!threadId || !messageCategory) {
    return ''
  }

  return String(messageCategory.id)
}

export const MessageFormContainer: React.FC<MessageFormContainerProps> = ({
  threadId,
  onSuccess,
  onCancel,
  replySubject,
  focusSubject,
  messageCategory,
  showParticipantsNotice,
}) => {
  const { t, i18n } = useTranslation('messages')
  const {
    data: categoryData,
    isLoading: categoriesLoading,
    isError: categoriesError,
    refetch: refetchCategories,
  } = useAPIGetResidentMessageCategories()
  const categories = categoryData?.categories ?? []
  const hasSelectableCategories = Boolean(categories.length)
  const shouldShowCategory = hasSelectableCategories || Boolean(messageCategory)
  const categorySelectionUnavailable =
    !threadId && !categoryData && (categoriesLoading || categoriesError)
  const validationSchema = getValidationSchema(
    t,
    !threadId && hasSelectableCategories
  )

  const [isSuccess, setIsSuccess] = useState(false)
  const [isUploading, setIsUploading] = React.useState(false)
  const subjectInputRef = useRef<HTMLInputElement>(null)

  const { loading: sending, error, send } = useSendMessage(threadId)

  const handleSubmit = async (
    values: MessageFormValues,
    helpers: FormikHelpers<MessageFormValues>
  ) => {
    const result = await send(values)

    if (result) {
      helpers.resetForm()
      setIsSuccess(true)
      onSuccess?.()
    } else {
      document
        .querySelector('#error-message')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  useEffect(() => {
    if (error)
      document
        .querySelector('#error-message')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [error])

  useEffect(() => {
    if (isSuccess)
      document
        .querySelector('#success-message')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [isSuccess])

  useEffect(() => {
    if (focusSubject && subjectInputRef.current) {
      subjectInputRef.current.focus()
    }
  }, [focusSubject])

  return (
    <div className="pb-6">
      {isSuccess ? (
        <div id="success-message">
          <Alert variant="success">{t('form.successTitle')}</Alert>
        </div>
      ) : (
        <Formik
          key={`form-${i18n.language}`}
          initialValues={{
            ...initialValues,
            subject: replySubject ?? '',
            messageCategoryId: getInitialMessageCategoryId({
              threadId,
              messageCategory,
            }),
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          validateOnMount={false}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {({ values, errors, setFieldValue }) => (
            <Form>
              <div className="flex justify-between mb-5">
                <Heading element="h2" size="xl" semibold>
                  {replySubject
                    ? t('view.replyToMessage')
                    : t('view.sendNewMessage')}
                </Heading>
                <Button
                  variant="text"
                  icon="x-mark"
                  onClick={() => {
                    setIsSuccess(false)
                    onCancel?.()
                  }}
                />
              </div>

              {error && (
                <div id="error-message">
                  <Alert variant="warning">{t('form.errorTitle')}</Alert>
                </div>
              )}
              {showParticipantsNotice && (
                <Alert variant="info">
                  {t('form.visibleToAllParticipants')}
                </Alert>
              )}
              {!sending ? (
                <div className="space-y-6 mt-6 text-black">
                  {(shouldShowCategory || categorySelectionUnavailable) && (
                    <MessageCategoryField
                      categories={categories}
                      disabled={Boolean(threadId)}
                      isLoading={
                        categorySelectionUnavailable && categoriesLoading
                      }
                      isError={categorySelectionUnavailable && categoriesError}
                      onRetry={() => refetchCategories()}
                      selectedCategory={messageCategory}
                      value={values.messageCategoryId}
                      validationError={
                        values.messageCategoryId
                          ? undefined
                          : errors.messageCategoryId
                      }
                      onChange={(value) =>
                        setFieldValue('messageCategoryId', value)
                      }
                    />
                  )}
                  <FormTextField
                    name="subject"
                    label={t('form.subject')}
                    inputRef={subjectInputRef}
                    maxLength={200}
                  />
                  <FormRichTextField name="message" label={t('form.message')} />
                  <FilesField
                    name="attachments"
                    label={t('form.attachments')}
                    className="mt-6"
                    isUploading={setIsUploading}
                  />
                  <div className="mt-4 text-right">
                    <Button
                      variant="text"
                      type="reset"
                      className="mr-2"
                      onClick={() => {
                        setIsSuccess(false)
                        onCancel?.()
                      }}
                    >
                      {t('form.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      disabled={isUploading || categorySelectionUnavailable}
                    >
                      {t('form.send')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <Loading />
                </div>
              )}
            </Form>
          )}
        </Formik>
      )}
    </div>
  )
}
